import { ImportedListing } from '../../_types/imported-listing'
import { DiscoverListing } from '../../_types/quickpass-discover'
import { formatMonthlyPrice } from '../../_utilities/format-price'
import { SavedListingPreviewItem } from './types'

/**
 * Builds the bedroom + city meta line for a preview tile. A missing bedroom
 * count falls back to the city alone (or a generic label when both are absent).
 *
 * @param {number | null} bedrooms - the listing's bedroom count
 * @param {string} city - the listing's city
 * @returns {string} the compact meta label
 */
const formatBedroomMeta = (bedrooms: number | null, city: string): string => {
  const cityLabel = city.trim()
  let bedroomLabel = ''
  if (bedrooms !== null && bedrooms >= 0) {
    bedroomLabel = bedrooms === 0 ? 'Studio' : `${bedrooms} bd`
  }

  if (bedroomLabel === '') {
    return cityLabel === '' ? 'Saved place' : cityLabel
  }

  return cityLabel === '' ? bedroomLabel : `${bedroomLabel} · ${cityLabel}`
}

/**
 * Formats a QuickCasa-network listing's monthly cost range into a compact
 * label, collapsing to a single value when the range has no spread.
 *
 * @param {number} costLowest - the lowest monthly cost in dollars
 * @param {number} costHighest - the highest monthly cost in dollars
 * @returns {string} the formatted price label
 */
const formatNetworkPrice = (costLowest: number, costHighest: number): string => {
  if (costLowest <= 0 && costHighest <= 0) {
    return 'Price unknown'
  }

  if (costHighest <= 0 || costLowest === costHighest) {
    return `$${costLowest.toLocaleString('en-CA')}/mo`
  }

  return `$${costLowest.toLocaleString(
    'en-CA',
  )}–$${costHighest.toLocaleString('en-CA')}/mo`
}

/**
 * Maps an imported listing to a uniform preview item.
 *
 * @param {ImportedListing} listing - the imported listing
 * @returns {SavedListingPreviewItem} the preview item
 */
const mapImportedToPreview = (
  listing: ImportedListing,
): SavedListingPreviewItem => {
  return {
    id: listing.id,
    to: `/imported-listings/${listing.id}`,
    imageUrl: listing.imageUrls[0] ?? null,
    title: listing.title,
    priceLabel: formatMonthlyPrice(listing.priceMonthly, listing.priceCurrency, {
      nullFallback: 'Price unknown',
    }),
    metaLabel: formatBedroomMeta(listing.bedrooms, listing.city),
  }
}

/**
 * Maps a QuickCasa-network (Discover) listing to a uniform preview item.
 *
 * @param {DiscoverListing} listing - the network listing
 * @returns {SavedListingPreviewItem} the preview item
 */
const mapNetworkToPreview = (
  listing: DiscoverListing,
): SavedListingPreviewItem => {
  return {
    id: listing.id,
    to: `/discover/${listing.id}`,
    imageUrl: listing.imageUrls[0] ?? null,
    title: listing.title,
    priceLabel: formatNetworkPrice(listing.costLowest, listing.costHighest),
    metaLabel: formatBedroomMeta(listing.bedrooms, listing.city),
  }
}

/**
 * Combines a renter's unarchived imported listings and hydrated network saves
 * into a single, capped list of preview items (imported first, then network).
 *
 * @param {ImportedListing[]} importedListings - the renter's unarchived imported listings
 * @param {DiscoverListing[]} networkListings - the hydrated network-saved listings
 * @param {number} limit - the maximum number of previews to return
 * @returns {SavedListingPreviewItem[]} the capped preview list
 */
const buildSavedListingPreviews = (
  importedListings: ImportedListing[],
  networkListings: DiscoverListing[],
  limit: number,
): SavedListingPreviewItem[] => {
  const imported = importedListings.map(mapImportedToPreview)
  const network = networkListings.map(mapNetworkToPreview)

  return [...imported, ...network].slice(0, limit)
}

export { buildSavedListingPreviews }
